import React, { useEffect, useState } from "react";
import { Form, Input, Button, Upload, message, Select, DatePicker } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import {
  IoMailOutline,
  IoPeopleOutline,
  IoDocumentTextOutline,
  IoCalendarOutline,
  IoSparklesOutline,
  IoCloudUploadOutline,
} from "react-icons/io5";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./ticketModule.css";
import getUserFromToken from "../../../utils/getUserFromToken";
import { useSelector, useDispatch } from "react-redux";
import { addSingleTicket } from "../../../redux/rtk/features/crm/ticket/ticketSlice";
import { useNavigate } from "react-router-dom";
import fileConfig from "../../../utils/fileConfig";
import moment from "moment";
import axios from "axios";

const { Dragger } = Upload;
const { Option } = Select;

const CreateTicketForm = () => {
  const { loading: tLoading } = useSelector((state) => state.ticket);
  const dispatch = useDispatch();
  const userId = getUserFromToken();
  const [form] = Form.useForm();
  const [email, setEmail] = useState("");
  const [usertype, setUserType] = useState(0);
  const [departmentId, setDepartmentId] = useState("");
  const [description, setDescription] = useState("");
  const [charCount, setCharCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomerEmail = async () => {
      try {
        const response = await axios.get(`/customer/${userId}`);
        setEmail(response.data.email);
        setUserType(parseInt(response.data.usertype, 10));
        setDepartmentId(response.data.departmentId);
        form.setFieldsValue({ email: response.data.email });
      } catch (error) {
        console.error("Error fetching customer email:", error);
      }
    };

    fetchCustomerEmail();
  }, [userId, form]);

  const onFinish = async (values) => {
    try {
      const formData = new FormData();
      formData.append("customerId", parseInt(userId));
      formData.append("email", values.email);
      formData.append("subject", values.subject);
      formData.append("description", description);
      formData.append("ticketCategoryId", 5);
      formData.append("ticketDate", moment().format("YYYY-MM-DD"));
      formData.append("submissionDate", moment().format("YYYY-MM-DD"));
      formData.append("usertype", usertype);
      formData.append("departmentId", departmentId);
      formData.append("postFor", values.postFor);
      // Optional: when the event being announced is actually happening —
      // distinct from the submission date above.
      if (values.eventDate) {
        formData.append("eventDate", moment(values.eventDate).format("YYYY-MM-DD"));
      }

      if (values.files) {
        if (fileConfig() !== "laravel") {
          for (let i = 0; i < values.files.length; i++) {
            formData.append("files", values.files[i].originFileObj);
          }
        } else {
          for (let i = 0; i < values.files.length; i++) {
            formData.append("files[]", values.files[i].originFileObj);
          }
        }
      }

      const resp = await dispatch(addSingleTicket(formData));

      if (resp.payload.message === "success") {
        form.resetFields();
        navigate(`/support/ticket`);
      } else {
        console.log("Something went wrong");
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleDescriptionChange = (content, delta, source, editor) => {
    const text = editor.getText();
    if (text.length <= 2000) {
      setDescription(content);
      setCharCount(text.length);
    } else {
      message.warning("Character limit reached (2000 characters)");
    }
  };

  const handleUpload = (files) => {
    message.success(`File added successfully`);
  };

  const today = moment();
  const postForOptions = [
    { label: "Public", value: 1 },
    { label: "Participants", value: 2 },
    { label: "Faculty/Staff", value: 3 },
  ];

  return (
    <div className="create-post-page">
      <div className="create-post-shell">
        <div className="create-post-header">
          <span className="create-post-eyebrow">
            <IoSparklesOutline /> Campus Pulse
          </span>
          <h3 className="create-post-title">Create a Post</h3>
          <p className="create-post-subtitle">
            Share news, events & opportunities with the UMT community.
          </p>
        </div>

        <Form
          name="ticketForm"
          className="create-post-form"
          onFinish={(values) => onFinish({ ...values, description })}
          form={form}
          onFinishFailed={onFinishFailed}
          layout="vertical"
        >
          <div className="create-post-row">
            <Form.Item
              name="email"
              label={
                <span className="create-post-label">
                  <IoMailOutline /> Email Address
                </span>
              }
              tooltip="We'll use this email to contact you"
              initialValue={email}
            >
              <Input size="large" placeholder="youremail@something.com" value={email} disabled />
            </Form.Item>

            <Form.Item
              name="postFor"
              label={
                <span className="create-post-label">
                  <IoPeopleOutline /> Post For
                </span>
              }
              rules={[
                {
                  required: true,
                  message: "Please select who this post is for",
                },
              ]}
            >
              <Select size="large" placeholder="Select target audience">
                {postForOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="subject"
            label={
              <span className="create-post-label">
                <IoDocumentTextOutline /> Subject
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please enter the subject",
              },
            ]}
          >
            <Input size="large" placeholder="Please write subject" />
          </Form.Item>

          <Form.Item
            name="description"
            label={
              <span className="create-post-label">
                <IoDocumentTextOutline /> Description
              </span>
            }
          >
            <ReactQuill
              theme="snow"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter the post content"
            />
            <div className="create-post-char-count">{charCount}/3000 characters</div>
          </Form.Item>

          <Form.Item
            name="eventDate"
            label={
              <span className="create-post-label">
                <IoCalendarOutline /> Event Date <span className="create-post-optional">(optional)</span>
              </span>
            }
            tooltip="Let readers know when the event you're announcing actually takes place"
          >
            <DatePicker
              size="large"
              className="create-post-datepicker"
              format="YYYY-MM-DD"
              placeholder="Select the event date"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item name="ticketDate" hidden initialValue={today}>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            name="files"
            label={
              <span className="create-post-label">
                <IoCloudUploadOutline /> Attachments
              </span>
            }
            valuePropName="fileList"
            getValueFromEvent={(e) => e && e.fileList}
          >
            <Dragger
              accept=".png,.jpg,.jpeg"
              multiple={true}
              beforeUpload={() => false}
              onChange={handleUpload}
              className="create-post-dragger"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Drag and drop files or click to select files</p>
              <p className="ant-upload-hint">Supported files types: PNG, JPG, JPEG</p>
            </Dragger>
          </Form.Item>

          <Form.Item className="create-post-submit-wrap">
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={tLoading}
              className="create-post-submit"
            >
              Create Post
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default CreateTicketForm;
